/**
 * The fetch layer.
 *
 * The JWT lives in sessionStorage, so closing the tab ends the session — it
 * narrows the XSS damage window at the cost of signing in again in a new tab.
 * Because it is a Bearer token and not a cookie, there is no CSRF surface and
 * no CSRF layer here (the project this was ported from carries one that never
 * did anything: its server never set the header it looked for).
 *
 * Every 2xx body under /api is validated against the envelope before it
 * reaches a caller, so a shape change fails loudly at one place instead of
 * surfacing as `undefined` three components deep.
 */

import type { ZodType } from 'zod';
import { sanitizeError } from '@/lib/errors';
import { type ApiEnvelope, ApiEnvelopeSchema, validateResponse } from '@/lib/schemas';

const TOKEN_KEY = 'skp_token';
const USER_KEY = 'skp_user';

let unauthorizedHandler: null | (() => void | Promise<void>) = null;

export class AuthError extends Error {
  readonly status: number;

  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'AuthError';
    this.status = 401;
  }
}

export interface ApiOpts extends RequestInit {
  /** send a JSON Content-Type (default true) */
  json?: boolean;
  /** milliseconds before the request is aborted (default 30 000) */
  timeout?: number;
}

type StatusError = Error & { status: number };

function createStatusError(message: string, status: number): StatusError {
  const error = new Error(message) as StatusError;
  error.status = status;
  return error;
}

function toHeaderRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...headers };
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === 'AbortError' || err.name === 'TimeoutError'
    : err instanceof Error && (err.name === 'AbortError' || err.message === 'The operation was aborted');
}

function isTimeoutReason(reason: unknown): boolean {
  return reason instanceof DOMException
    ? reason.name === 'TimeoutError'
    : reason instanceof Error && reason.name === 'TimeoutError';
}

/**
 * Re-throws an abort as what it actually was. A timeout becomes a readable
 * error; a caller's own cancellation stays an AbortError so TanStack Query
 * swallows it instead of rendering a failure.
 */
function throwAbortReason(signal: AbortSignal): never {
  const { reason } = signal;
  if (isTimeoutReason(reason)) throw new Error(sanitizeError('Connection timeout'));
  if (reason instanceof Error) throw reason;
  throw new DOMException('The operation was aborted', 'AbortError');
}

/* --------------------------------------------------------------- session */
export function getToken(): string | null {
  try { return sessionStorage.getItem(TOKEN_KEY); }
  catch { return null; }
}

export function getUser<T = unknown>(): T | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch { return null; }
}

export function setSession(token: string, user: unknown): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch { /* private window with storage disabled — the session is in memory only */ }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* nothing to clear */ }
}

/** Lets the auth store own what a 401 does without api.ts importing the store. */
export function registerUnauthorizedHandler(handler: null | (() => void | Promise<void>)): void {
  unauthorizedHandler = handler;
}

/* ----------------------------------------------------------------- fetch */
export async function apiFetch<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const { json = true, timeout = 30_000, headers, signal: providedSignal, ...rest } = opts;
  const token = getToken();
  const method = (rest.method || 'GET').toUpperCase();

  const finalHeaders: Record<string, string> = {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...toHeaderRecord(headers),
  };

  const timeoutSignal = AbortSignal.timeout(timeout);
  const signal = providedSignal ? AbortSignal.any([providedSignal, timeoutSignal]) : timeoutSignal;

  let res: Response;
  try {
    res = await fetch(path, { ...rest, method, headers: finalHeaders, signal });
  } catch (err) {
    if (isAbortError(err)) throwAbortReason(signal);
    throw new Error(sanitizeError(err instanceof Error ? err.message : 'Unknown error'));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // `message` is the envelope; `detail` is FastAPI's own shape, kept as a
    // fallback for anything that escapes src/envelope.py
    const raw = body.message || body.error || body.detail || `HTTP ${res.status}`;
    const message = sanitizeError(typeof raw === 'string' ? raw : JSON.stringify(raw));

    if (res.status === 401) {
      if (token) {
        if (unauthorizedHandler) await unauthorizedHandler();
        else clearSession();
      }
      throw new AuthError(message);
    }
    throw createStatusError(message, res.status);
  }

  let text: string;
  try {
    text = await res.text();
  } catch (err) {
    if (isAbortError(err)) throwAbortReason(signal);
    throw new Error(sanitizeError(err instanceof Error ? err.message : 'Unknown error'));
  }

  // 204 from DELETE, and the text/plain endpoints, have nothing to validate
  if (!text) return undefined as T;
  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    return text as unknown as T;
  }

  try {
    return validateResponse(ApiEnvelopeSchema, JSON.parse(text)) as T;
  } catch (err) {
    throw new Error(sanitizeError(err instanceof Error ? err.message : 'Unknown error'));
  }
}

/**
 * The normal entry point: unwraps the envelope and, when given a schema,
 * validates `data` against it.
 */
export async function apiFetchData<T>(url: string, opts?: ApiOpts, schema?: ZodType<T>): Promise<T> {
  const res = await apiFetch<ApiEnvelope>(url, opts);
  if (res === undefined) return undefined as T;
  if (!res.success) throw new Error(sanitizeError(res.message || 'Unknown error'));
  return schema ? validateResponse(schema, res.data) : (res.data as T);
}
